import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, Menu, X, Facebook, Linkedin, Instagram } from 'lucide-react';

const Navbar = () => {
  const { language, toggleLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const navItems = [
    { id: 'home', label: { fr: 'Accueil', en: 'Home' } },
    { id: 'about', label: { fr: 'Bureau', en: 'Office' } },
    { id: 'projects', label: { fr: 'Projets', en: 'Projects' } },
    { id: 'services', label: { fr: 'Services', en: 'Services' } },
    { id: 'contact', label: { fr: 'Contact', en: 'Contact' } },
  ];

  const socialLinks = [
    { href: 'https://www.facebook.com/myihome.ch', icon: Facebook, label: 'Facebook' },
    { href: 'https://www.linkedin.com/company/85937665/', icon: Linkedin, label: 'LinkedIn' },
    { href: 'https://www.instagram.com/my_ihome_sa/', icon: Instagram, label: 'Instagram' },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo - white when at top, normal when scrolled */}
          <div className="flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_archspace-12/artifacts/kmu49d1s_myiHome_Masters-logo_web.png" 
              alt="my iHome" 
              className={`h-12 w-auto transition-all duration-500 ${
                isScrolled ? '' : 'brightness-0 invert'
              }`}
            />
          </div>

          {/* Desktop Navigation - hidden until lg (1024px) */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm tracking-wide transition-colors relative group ${
                  isScrolled 
                    ? 'text-neutral-600 hover:text-neutral-900' 
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {language === 'fr' ? item.label.fr : item.label.en}
                <span className={`absolute -bottom-1 left-0 w-0 h-px transition-all duration-300 group-hover:w-full ${
                  isScrolled ? 'bg-neutral-500' : 'bg-white'
                }`}></span>
              </button>
            ))}
            
            {/* Separator */}
            <div className={`w-px h-5 ${isScrolled ? 'bg-neutral-300' : 'bg-white/30'}`}></div>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`transition-colors ${
                    isScrolled 
                      ? 'text-neutral-400 hover:text-neutral-700' 
                      : 'text-white/70 hover:text-white'
                  }`}
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-1 text-sm transition-colors ${
                isScrolled 
                  ? 'text-neutral-500 hover:text-neutral-800' 
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'fr' ? 'EN' : 'FR'}</span>
            </button>
          </div>

          {/* Mobile menu button - visible until lg */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden transition-colors ${
              isScrolled ? 'text-neutral-800' : 'text-white'
            }`}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - visible until lg */}
      {mobileMenuOpen && (
        <div className={`lg:hidden border-t ${
          isScrolled 
            ? 'bg-white/98 border-neutral-200' 
            : 'bg-black/90 border-white/10'
        }`}>
          <div className="px-6 py-8 space-y-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`block text-lg transition-colors ${
                  isScrolled 
                    ? 'text-neutral-600 hover:text-neutral-900' 
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {language === 'fr' ? item.label.fr : item.label.en}
              </button>
            ))}
            
            {/* Mobile Social Links */}
            <div className={`flex items-center space-x-6 pt-4 border-t ${
              isScrolled ? 'border-neutral-200' : 'border-white/10'
            }`}>
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`transition-colors ${
                    isScrolled 
                      ? 'text-neutral-500 hover:text-neutral-800' 
                      : 'text-white/70 hover:text-white'
                  }`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Mobile Language Toggle */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-2 transition-colors ${
                isScrolled 
                  ? 'text-neutral-500 hover:text-neutral-800' 
                  : 'text-white/80 hover:text-white'
              }`}
            >
              <Globe className="w-5 h-5" />
              <span>{language === 'fr' ? 'English' : 'Français'}</span>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
